/**
 * Authentication Redux Slice
 * 
 * DRY principle - handleAsync helper ile pending/fulfilled/rejected logic tek yerde.
 * Clean, minimal authentication state management.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../../services/authService';
import { setToken, removeToken, getToken } from '../../utils/storageUtils';

// ============================================================================
// ASYNC THUNKS
// ============================================================================

/**
 * Login async thunk
 * Calls backend, stores token, returns user data
 */
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      // Store token in localStorage
      setToken(response.token);
      return response; // { token, user }
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Register async thunk
 * Creates new user account
 * Not: Eğer response token dönerse, localStorage'a kaydediyoruz (kullanıcı otomatik login gibi).
 */
export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);
      // Eğer backend response token döndürüyorsa, setToken ile sakla:
      if (response && response.token) {
        setToken(response.token);
      }
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Get current user async thunk
 * Fetches user data from backend using stored token
 * Used on app initialization to restore auth state
 */
export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error('No token found');
      }
      return await authService.getCurrentUser();
    } catch (error) {
      // Remove invalid token
      removeToken();
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// DRY HELPER: Generic Async Handler
// ============================================================================

/**
 * Generic async thunk handler
 * Reduces code duplication for pending/fulfilled/rejected cases
 * 
 * @param {Object} builder - Redux toolkit builder
 * @param {Function} thunk - Async thunk to handle
 * @param {Function} onSuccess - Optional success handler (state, action) => void
 */
const handleAsync = (builder, thunk, onSuccess) => {
  builder
    .addCase(thunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state.loading = false;
      if (onSuccess) {
        onSuccess(state, action);
      }
    })
    .addCase(thunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || 'Bir hata oluştu';
    });
};

// ============================================================================
// SLICE
// ============================================================================

// Get initial token from localStorage
const initialToken = getToken();

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: initialToken,
    isAuthenticated: !!initialToken, // If token exists, consider authenticated
    permissions: [], // Phase 5: M5.T5.1 - User's permission codes
    menu: [], // Phase 5: M5.T5.1 - Hierarchical menu structure
    loading: false,
    error: null
  },
  reducers: {
    /**
     * Logout reducer
     * Clears user state and removes token from localStorage
     */
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.permissions = []; // Phase 5: Clear permissions
      state.menu = []; // Phase 5: Clear menu
      state.error = null;
      removeToken();
    },
    
    /**
     * Clear error reducer
     * Clears error message (used after showing alertify)
     */
    clearError: (state) => {
      state.error = null;
    },
    
    /**
     * Update user reducer
     * Updates user data in Redux state (after successful profile update)
     */
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    // Login: Set user, token, isAuthenticated
    handleAsync(builder, login, (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      // Permissions and menu will be fetched by getCurrentUser after login
    });
    
    // Register: Set user, token, isAuthenticated (auto-login after register)
    handleAsync(builder, register, (state, action) => {
      // Backend returns { token, user } on successful registration
      if (action.payload.token && action.payload.user) {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      }
    });
    
    // Get Current User: Set user, permissions, menu, isAuthenticated
    // Phase 5: M5.T5.1 - Backend now returns { user, menu }
    handleAsync(builder, getCurrentUser, (state, action) => {
      state.user = action.payload.user;
      state.permissions = action.payload.user?.permissions || [];
      state.menu = action.payload.menu || [];
      state.isAuthenticated = true;
    });
  }
});

// ============================================================================
// EXPORTS
// ============================================================================

export const { logout, clearError, updateUser } = authSlice.actions;
export default authSlice.reducer;

